import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../configuration";
import { useSignalR } from "../../../hooks";
import { EditIssueContextDto, FetchError } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";

type Variables = {
  issueGuid: string;
  contextActionItemGuid: string | null;
  assignTeamSlug: string | undefined;
  createdByUserId: string;
};

const useEditIssueContextRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editIssueContextRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditIssueContextDto = {
      contextActionItemGuid: variables.contextActionItemGuid,
    };

    const response = await fetch(`${configuration.apiRootUrl}/issues/${variables.issueGuid}/context`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editIssueContextRequest };
};

const useEditIssueContext = () => {
  const { editIssueContextRequest } = useEditIssueContextRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editIssueContextRequest,
    onSuccess: (_data, variables) => {
      if (variables.assignTeamSlug) {
        signalR.sendRefetchTeamIssues(variables.assignTeamSlug);
      }

      signalR.sendRefetchIssue(variables.issueGuid);
      signalR.sendRefetchComments(variables.issueGuid);
      signalR.sendRefetchUserIssues(variables.createdByUserId);

      if (variables.contextActionItemGuid) {
        signalR.sendRefetchActionItem(variables.contextActionItemGuid);
      }
    },
    onSettled: (_data, _error, variables) => {
      if (variables.assignTeamSlug) {
        queryClient.invalidateQueries(queryKeys.issues.getIssues(variables.assignTeamSlug, undefined));
      }

      queryClient.invalidateQueries(queryKeys.issues.getIssueDetail(variables.issueGuid));
    },
  });
};

export { useEditIssueContext };
